// @ts-check

import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import { omitTypename } from "src/util/graphql";

/**
 * @param {{
 *   getQuery: import("@apollo/client").DocumentNode,
 *   updateMutation: import("@apollo/client").DocumentNode,
 * }} config
 */
export const useOrgConfig = ({ getQuery, updateMutation }) => {
  const { data, loading } = useQuery(getQuery, {
    fetchPolicy: "cache-first",
  });

  const [updateConfig, { loading: updating }] = useMutation(updateMutation);

  const config = useMemo(
    () => omitTypename(/** @type {any} */ (data)?.config),
    [data]
  );

  const update = useCallback(
    async (newConfig) => {
      if (loading) {
        return false;
      }

      try {
        await updateConfig({
          variables: { input: { config: newConfig } },
          update: (cache, { data: updateData }) => {
            const d = /** @type {any} */ (updateData);
            if (!d?.config) {
              return;
            }

            const existingData = cache.readQuery({ query: getQuery });

            if (existingData) {
              cache.writeQuery({
                query: getQuery,
                data: { config: d.config },
              });
            }
          },
        });
      } catch {
        return false;
      }

      return true;
    },
    [updateConfig, loading, getQuery]
  );

  return {
    loading,
    config: config?.config,
    createdBy: config?.createdBy,
    createdAt: config?.createdAt,
    update,
    updating,
  };
};
